import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, FileText, Heart, Mail } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
type LegalModalProps = {
  open: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | null;
};
export function LegalModal({
  open,
  onClose,
  type
}: LegalModalProps) {
  const {
    t
  } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, onClose]);
  const isPrivacy = type === 'privacy';
  const title = isPrivacy ? t.legal.privacyTitle : t.legal.termsTitle;
  const Icon = isPrivacy ? ShieldCheck : FileText;
  const content = isPrivacy ? t.legal.privacy : t.legal.terms;
  return <AnimatePresence data-id="element-345">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" aria-modal="true" role="dialog" aria-labelledby="legal-modal-title" data-id="element-346">
          <motion.button type="button" aria-label={t.legal.close} onClick={onClose} className="absolute inset-0 bg-lavender-900/50 backdrop-blur-sm cursor-default" data-id="element-347" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-lavender-100" data-id="element-348">
            <div className="flex-shrink-0 px-6 sm:px-8 pt-6 pb-5 border-b border-lavender-100 bg-gradient-to-r from-cream-50 to-lavender-50 flex items-center justify-between gap-4" data-id="element-349">
              <div className="flex items-center gap-3" data-id="element-350">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-lavender-100" data-id="element-351">
                  <Icon className="w-5 h-5 text-lavender-600" data-id="element-352" />
                </div>
                <h2 id="legal-modal-title" className="font-serif text-2xl text-gray-900" data-id="element-353">
                  {title}
                </h2>
              </div>
              <button onClick={onClose} aria-label={t.legal.close} className="w-9 h-9 rounded-full bg-white hover:bg-lavender-100 flex items-center justify-center shadow-sm border border-lavender-100 transition-colors" data-id="element-354">
                <X className="w-4 h-4 text-gray-600" data-id="element-355" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 text-gray-700 leading-relaxed" data-id="element-356">
              <div className="space-y-3 text-[15px]" data-id="element-357">
                <p data-id="element-358">{content.intro}</p>
                {content.sections.map((section, i) => <div key={i} data-id="element-359">
                    <h3 className="font-serif text-lg font-semibold text-gray-900 mt-6 mb-2" data-id="element-360">
                      {section.title}
                    </h3>
                    {'list' in section && section.list ? <ul className="list-disc pl-5 space-y-1.5" data-id="element-361">
                        {section.list.map((item, j) => <li key={j} data-id="element-362">{item}</li>)}
                      </ul> : <p data-id="element-363">
                        {(section as {
                  body: string;
                }).body}
                      </p>}
                  </div>)}
              </div>
            </div>

            <div className="flex-shrink-0 px-6 sm:px-8 py-4 border-t border-lavender-100 bg-cream-50 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-364">
              <p className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-365">
                <Heart className="w-3.5 h-3.5 text-blush-500 fill-blush-500" data-id="element-366" />
                {t.legal.lastUpdated}: {new Date().getFullYear()}
              </p>
              <button onClick={onClose} className="inline-flex items-center justify-center px-5 py-2 text-sm font-medium rounded-full text-white bg-lavender-500 hover:bg-lavender-600 shadow-sm transition-all" data-id="element-367">
                {t.legal.gotIt}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}